import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {concatLatestFrom} from '@ngrx/operators';
import {select, Store} from '@ngrx/store';
import {EMPTY, of, switchMap, mergeWith} from 'rxjs';
import {delay, filter, map, mergeMap} from 'rxjs/operators';
import {SimulatedDateService} from 'src/app/services/simulated-date/simulated-date.service';
import {CompareHelper} from '../../cd-common/helper/compare-helper';
import {NotificationBroadcastService} from '../../services/notification-broadcast/notification-broadcast.service';
import {CustomEventNotificationCountChanged} from '../../util/monitoring/custom-events';
import {MonitoringHelper} from '../../util/monitoring/monitoring-helper';
import {notificationMouseEnter, notificationCreate, notificationDelete, notificationDeleteAll, notificationMouseLeave} from './notification.actions';
import {selectAllNotifications, selectNotificationsHovered} from './notification.selectors';

@Injectable()
export class NotificationEffects {
  autoRemoveNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(notificationCreate),
      mergeMap(notificationCreateAction =>
        of(null).pipe(
          mergeWith(this.actions$.pipe(ofType(notificationMouseEnter)), this.actions$.pipe(ofType(notificationMouseLeave))),
          concatLatestFrom(() => this.store.pipe(select(selectNotificationsHovered))),
          switchMap(([action, initiallyHovered]) => {
            if (action?.type === notificationMouseEnter.type) {
              return EMPTY;
            }
            const minimumDelay = notificationCreateAction.notification.removeAfterSec * 1000;
            if (action?.type === notificationMouseLeave.type) {
              const notificationCreated = new Date(notificationCreateAction.notification.createdTs);
              const now = this.simulatedDateService.getSimulatedDate();
              const notificationAge = now.getTime() - notificationCreated.getTime();
              const minimumDelayAfterMouseLeave = 2000;
              const delayTime = Math.max(minimumDelayAfterMouseLeave, minimumDelay - notificationAge);
              return of(notificationDelete({notification: notificationCreateAction.notification, doBroadcast: false, onlyRemoveInBell: true})).pipe(delay(delayTime));
            }
            return initiallyHovered ? EMPTY : of(notificationDelete({notification: notificationCreateAction.notification, doBroadcast: false, onlyRemoveInBell: true})).pipe(delay(minimumDelay));
          })
        )
      )
    )
  );

  broadcastRemoveNotification$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(notificationDelete),
        filter(n => n.doBroadcast ?? true),
        concatLatestFrom(() => this.store.pipe(select(selectAllNotifications))),
        mergeMap(([a, notifications]) => {
          this.notificationBroadcastService.broadcastRemoveNotification(a.notification);
          MonitoringHelper.monitorCustomEvent(new CustomEventNotificationCountChanged({source: 'removeOne', newCount: notifications.length}));
          return EMPTY;
        })
      ),
    {dispatch: false}
  );

  broadcastRemoveAllNotifications$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(notificationDeleteAll),
        filter(a => a.doBroadcast ?? true),
        mergeMap(() => {
          this.notificationBroadcastService.broadcastRemoveAllNotifications();
          MonitoringHelper.monitorCustomEvent(new CustomEventNotificationCountChanged({source: 'removeAll', newCount: 0}));
          return EMPTY;
        })
      ),
    {dispatch: false}
  );

  applyNotificationDeleteAllFromBroadcast$ = createEffect(() => this.notificationBroadcastService.subscribeDeleteAllNotifications().pipe(map(() => notificationDeleteAll({doBroadcast: false}))));

  applyNotificationDeleteFromBroadcast$ = createEffect(() =>
    this.notificationBroadcastService.subscribeDeleteNotification().pipe(
      concatLatestFrom(() => this.store.pipe(select(selectAllNotifications))),
      mergeMap(([notification, notifications]) => {
        const notificationToDelete = notifications.find(n => CompareHelper.equalsObject(notification, NotificationBroadcastService.serialize({...n})));
        if (notificationToDelete) {
          return of(notificationDelete({notification: notificationToDelete, doBroadcast: false}));
        }
        return EMPTY;
      })
    )
  );

  monitorAddedNotification$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(notificationCreate),
        concatLatestFrom(() => this.store.pipe(select(selectAllNotifications))),
        mergeMap(([, allNotifications]) => {
          MonitoringHelper.monitorCustomEvent(new CustomEventNotificationCountChanged({source: 'addOne', newCount: allNotifications.length}));
          return EMPTY;
        })
      ),
    {dispatch: false}
  );

  constructor(
    private actions$: Actions,
    private store: Store,
    private notificationBroadcastService: NotificationBroadcastService,
    private simulatedDateService: SimulatedDateService
  ) {}
}
